import type { PlayerEconomyState } from '../../domain';

type BackendEnvelope = {
  playerEconomy?: PlayerEconomyState;
};

export type BackendLeaderboardEntry = {
  rank: number;
  name: string;
  score: number;
  isPlayer?: boolean;
};

export type BackendSeasonLeaderboardResponse = {
  seasonId: string;
  seasonTitle: string;
  seasonEndsAtIso: string;
  entries: BackendLeaderboardEntry[];
};

export type BackendAllTimeLeaderboardResponse = {
  entries: BackendLeaderboardEntry[];
};

function getBackendBaseUrl(): string | null {
  const envBase = process.env.REACT_APP_BACKEND_API_BASE;
  if (typeof envBase === 'string' && envBase.trim() !== '') {
    return envBase.trim().replace(/\/+$/, '');
  }
  return null;
}

function getPlayerId(): string {
  return 'local-player';
}

async function backendGet<T>(path: string): Promise<T | null> {
  const base = getBackendBaseUrl();
  if (base == null) {
    return null;
  }
  const res = await fetch(`${base}${path}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    return null;
  }
  return (await res.json()) as T;
}

export async function loadPlayerEconomyFromBackend(): Promise<PlayerEconomyState | null> {
  const payload = await backendGet<BackendEnvelope>(
    `/player-state?playerId=${encodeURIComponent(getPlayerId())}`,
  );
  return payload?.playerEconomy ?? null;
}

export async function savePlayerEconomyToBackend(state: PlayerEconomyState): Promise<void> {
  const base = getBackendBaseUrl();
  if (base == null) {
    return;
  }
  const res = await fetch(`${base}/player-state`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      playerId: getPlayerId(),
      playerEconomy: state,
    }),
  });
  if (!res.ok) {
    throw new Error(`savePlayerEconomyToBackend: HTTP ${res.status}`);
  }
}

export async function fetchSeasonLeaderboardFromBackend(
  seasonId: string,
): Promise<BackendSeasonLeaderboardResponse | null> {
  return backendGet<BackendSeasonLeaderboardResponse>(
    `/leaderboards/season?seasonId=${encodeURIComponent(seasonId)}&playerId=${encodeURIComponent(getPlayerId())}`,
  );
}

export async function fetchAllTimeLeaderboardFromBackend(): Promise<BackendAllTimeLeaderboardResponse | null> {
  return backendGet<BackendAllTimeLeaderboardResponse>(
    `/leaderboards/all-time?playerId=${encodeURIComponent(getPlayerId())}`,
  );
}

export async function fetchCurrentSeasonFromBackend(): Promise<{
  seasonId: string;
  seasonTitle: string;
  seasonEndsAtIso: string;
} | null> {
  return backendGet<{ seasonId: string; seasonTitle: string; seasonEndsAtIso: string }>(
    '/season/current',
  );
}
